import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import { createRulesUrl } from "@components/settings/Rules/lib/rulesUrl";
import { BulkUpdateParams } from "../../transactions/TransactionsBulkActions/lib";
import { BulkUpdateFailureCounts } from "../../transactions/TransactionsBulkActions/bulkUpdateFailure";

export const SIMILAR_TRANSACTIONS_UPDATE_TOAST_MESSAGE_KEY =
    "counterparty-assign-toast";

const VALUE_LABELS = {
    categoryId: "category",
    counterpartyId: "counterparty",
    customerId: "customer",
    coa: "cash ledger",
    accrualCoa: "accrual account",
    classes: "class",
    entity: "entity",
    memo: "memo",
    taxQuestionAnswers: "tax question",
    pairedTransferId: "transfer",
    excludeFrom1099: "1099 exclusion",
} satisfies Record<BulkUpdateParams["update"], string>;

export function getSimilarTransactionsUpdateValueLabel({
    update,
    count,
}: {
    update: keyof typeof VALUE_LABELS;
    count: number;
}): string {
    const label = VALUE_LABELS[update];
    if (count === 1 || update === "classes") {
        return label;
    }
    return pluralize(label, count);
}

export function getSimilarTransactionsUpdateFailureCopy({
    update,
    failure: { succeededCount, failedCount },
}: {
    update: BulkUpdateParams["update"];
    failure: BulkUpdateFailureCounts;
}): string {
    if (succeededCount === 0) {
        return "Failed to update similar transactions";
    }
    const verb = update === "entity" ? "Moved" : "Updated";
    const failedCopy =
        failedCount === null ? "the rest failed" : `${failedCount} failed`;
    return `${verb} ${pluralizeWithCount("transaction", succeededCount)}, ${failedCopy}`;
}

export function getSimilarTransactionsRuleCopy({
    ruleCount,
}: {
    ruleCount: number;
}): string {
    if (ruleCount === 0) {
        return "with no rule created.";
    }
    if (ruleCount === 1) {
        return "with a rule created.";
    }
    return `with ${ruleCount} rules created.`;
}

export function getSimilarTransactionsRuleToastAction(
    ruleIds: string[],
): { label: string; path: string } | null {
    if (ruleIds.length === 0) {
        return null;
    }
    if (ruleIds.length === 1) {
        return {
            label: "View rule",
            path: createRulesUrl({ ruleId: ruleIds[0] }),
        };
    }
    return {
        label: "View rules",
        path: createRulesUrl(),
    };
}
